/**
 * Space Preferences Hook
 *
 * Manages per-identity Space preferences (favorites, theme choice,
 * notification overrides, activity feed controls), the alias-wide Space notification defaults, and the
 * alias notification detail settings (shared with conversations), with
 * optimistic updates and server synchronisation.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  applySpaceNotificationOverridesPatch,
  createApiClient,
  DEFAULT_NOTIFICATION_DISPLAY,
  DEFAULT_SPACE_NOTIFICATION_DEFAULTS,
  normalizeSpaceActivityPreferences,
  resolveNotificationDisplay,
  type NotificationDisplaySettings,
  type SpaceActivityPreferences,
  type SpaceNotificationDefaults,
  type SpaceNotificationOverridesPatch,
  type SpacePreferences,
  type SpacePreferencesPatch,
} from '@adieuu/shared';
import { useTranslation } from 'react-i18next';
import { useIdentity } from './useIdentity';
import { useAppConfig } from '../config';
import { useToast } from '../components/Toast';
import { emitSpaceNotificationSettingsSaved } from '../services/spaceNotificationSettingsEvents';

type PreferencesMap = Record<string, SpacePreferences>;

interface SpacePreferencesContextValue {
  preferences: PreferencesMap;
  loading: boolean;
  toggleFavorite: (spaceId: string, favorited: boolean) => Promise<void>;
  /** Save (boolean) or clear (null) this alias's theme choice for a Space. */
  setUseSpaceTheme: (spaceId: string, useSpaceTheme: boolean | null) => Promise<void>;
  /** Spaces this alias has a saved theme choice for (only ones it can still open). */
  getThemeOverrideCount: () => Promise<number>;
  /** Clears every saved theme choice for this alias; resolves to the count cleared. */
  resetThemeOverrides: () => Promise<number>;
  /** Alias-wide defaults every Space inherits until customized. */
  notificationDefaults: SpaceNotificationDefaults;
  setNotificationDefaults: (defaults: SpaceNotificationDefaults) => Promise<void>;
  /** Merge a notification override patch for a Space (`null` values inherit again). */
  setSpaceNotifications: (spaceId: string, patch: SpaceNotificationOverridesPatch) => Promise<void>;
  /** Save (replacing) or clear (null) this alias's activity feed controls for a Space. */
  setActivityPreferences: (spaceId: string, activity: SpaceActivityPreferences | null) => Promise<void>;
  /** Which details conversation and Space message notifications reveal. */
  notificationDisplay: NotificationDisplaySettings;
  setNotificationDisplay: (display: NotificationDisplaySettings) => Promise<void>;
}

const SpacePreferencesContext = createContext<SpacePreferencesContextValue | null>(null);

interface SpacePreferencesProviderProps {
  children: ReactNode;
}

export function SpacePreferencesProvider({ children }: SpacePreferencesProviderProps) {
  const { status: identityStatus } = useIdentity();
  const { apiBaseUrl } = useAppConfig();
  const { t } = useTranslation();
  const toast = useToast();

  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  const isLoggedIn = identityStatus === 'logged_in';

  const [preferences, setPreferences] = useState<PreferencesMap>({});
  const [loading, setLoading] = useState(false);
  const [notificationDefaults, setNotificationDefaultsState] = useState<SpaceNotificationDefaults>(
    DEFAULT_SPACE_NOTIFICATION_DEFAULTS,
  );
  const notificationDefaultsRef = useRef(notificationDefaults);
  notificationDefaultsRef.current = notificationDefaults;
  const defaultsRequestSeq = useRef(0);
  const [notificationDisplay, setNotificationDisplayState] = useState<NotificationDisplaySettings>(
    DEFAULT_NOTIFICATION_DISPLAY,
  );
  const notificationDisplayRef = useRef(notificationDisplay);
  notificationDisplayRef.current = notificationDisplay;
  const displayRequestSeq = useRef(0);

  useEffect(() => {
    if (!isLoggedIn) {
      setNotificationDefaultsState(DEFAULT_SPACE_NOTIFICATION_DEFAULTS);
      setNotificationDisplayState(DEFAULT_NOTIFICATION_DISPLAY);
      return;
    }
    let cancelled = false;
    const seq = defaultsRequestSeq.current;
    const displaySeq = displayRequestSeq.current;
    api.identity
      .getNotificationSettings()
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        // A save started meanwhile wins over the initial load.
        if (seq === defaultsRequestSeq.current) setNotificationDefaultsState(res.data.spaces);
        if (displaySeq === displayRequestSeq.current) {
          setNotificationDisplayState(resolveNotificationDisplay(res.data.display));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, api]);

  useEffect(() => {
    if (!isLoggedIn) {
      setPreferences({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    api.spaces
      .listPreferences()
      .then((res) => {
        if (cancelled) return;
        if (res.data) {
          const map: PreferencesMap = {};
          for (const pref of res.data) {
            map[pref.spaceId] = pref;
          }
          setPreferences(map);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, api]);

  const applyOptimistic = useCallback((spaceId: string, patch: SpacePreferencesPatch) => {
    setPreferences((prev) => {
      const existing = prev[spaceId];
      const useSpaceTheme =
        patch.useSpaceTheme === undefined ? existing?.useSpaceTheme : patch.useSpaceTheme;
      const notifications = patch.notifications
        ? applySpaceNotificationOverridesPatch(existing?.notifications, patch.notifications)
        : existing?.notifications;
      const activity =
        patch.activity === undefined ? existing?.activity : normalizeSpaceActivityPreferences(patch.activity);
      return {
        ...prev,
        [spaceId]: {
          id: existing?.id ?? '',
          spaceId,
          favorited: patch.favorited ?? existing?.favorited ?? false,
          lurking: patch.lurking ?? existing?.lurking ?? false,
          lurkingSince: existing?.lurkingSince ?? null,
          ...(typeof useSpaceTheme === 'boolean' ? { useSpaceTheme } : {}),
          ...(notifications ? { notifications } : {}),
          ...(activity ? { activity } : {}),
        },
      };
    });
  }, []);

  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const themeRequestSeq = useRef<Record<string, number>>({});
  const notifyRequestSeq = useRef<Record<string, number>>({});
  const activityRequestSeq = useRef<Record<string, number>>({});

  const toggleFavorite = useCallback(
    async (spaceId: string, favorited: boolean) => {
      const prev = preferences[spaceId];
      applyOptimistic(spaceId, { favorited });

      try {
        const res = await api.spaces.updatePreferences(spaceId, { favorited });
        if (res.data) {
          setPreferences((p) => ({
            ...p,
            [spaceId]: res.data!,
          }));
        }
        toast.success(
          favorited
            ? t('spaces.favoriteAddedToast', 'Added to Favourites')
            : t('spaces.favoriteRemovedToast', 'Removed from Favourites'),
        );
      } catch {
        if (prev) {
          setPreferences((p) => ({ ...p, [spaceId]: prev }));
        } else {
          setPreferences((p) => {
            const next = { ...p };
            delete next[spaceId];
            return next;
          });
        }
      }
    },
    [preferences, api, applyOptimistic, toast, t],
  );

  const setUseSpaceTheme = useCallback(
    async (spaceId: string, useSpaceTheme: boolean | null) => {
      const prev = preferencesRef.current[spaceId];
      const seq = (themeRequestSeq.current[spaceId] ?? 0) + 1;
      themeRequestSeq.current[spaceId] = seq;
      const isLatest = () => themeRequestSeq.current[spaceId] === seq;
      applyOptimistic(spaceId, { useSpaceTheme });

      try {
        const res = await api.spaces.updatePreferences(spaceId, { useSpaceTheme });
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'save failed');
        const saved = res.data;
        if (isLatest()) setPreferences((p) => ({ ...p, [spaceId]: saved }));
      } catch {
        if (!isLatest()) return;
        setPreferences((p) => {
          const next = { ...p };
          if (prev) next[spaceId] = prev;
          else delete next[spaceId];
          return next;
        });
        toast.error(t('spaces.themeToggle.saveFailed', 'Could not save your theme choice for this Space.'));
      }
    },
    [api, applyOptimistic, toast, t],
  );

  const setActivityPreferences = useCallback(
    async (spaceId: string, activity: SpaceActivityPreferences | null) => {
      const prev = preferencesRef.current[spaceId];
      const seq = (activityRequestSeq.current[spaceId] ?? 0) + 1;
      activityRequestSeq.current[spaceId] = seq;
      const isLatest = () => activityRequestSeq.current[spaceId] === seq;
      applyOptimistic(spaceId, { activity });

      try {
        const res = await api.spaces.updatePreferences(spaceId, { activity });
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'save failed');
        const saved = res.data;
        if (isLatest()) setPreferences((p) => ({ ...p, [spaceId]: saved }));
      } catch {
        if (!isLatest()) return;
        setPreferences((p) => {
          const next = { ...p };
          if (prev) next[spaceId] = prev;
          else delete next[spaceId];
          return next;
        });
        toast.error(t('spaces.activity.saveFailed'));
      }
    },
    [api, applyOptimistic, toast, t],
  );

  const setSpaceNotifications = useCallback(
    async (spaceId: string, patch: SpaceNotificationOverridesPatch) => {
      const prev = preferencesRef.current[spaceId];
      const seq = (notifyRequestSeq.current[spaceId] ?? 0) + 1;
      notifyRequestSeq.current[spaceId] = seq;
      const isLatest = () => notifyRequestSeq.current[spaceId] === seq;
      applyOptimistic(spaceId, { notifications: patch });

      try {
        const res = await api.spaces.updatePreferences(spaceId, { notifications: patch });
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'save failed');
        const saved = res.data;
        if (isLatest()) setPreferences((p) => ({ ...p, [spaceId]: saved }));
        emitSpaceNotificationSettingsSaved(spaceId);
      } catch {
        if (!isLatest()) return;
        setPreferences((p) => {
          const next = { ...p };
          if (prev) next[spaceId] = prev;
          else delete next[spaceId];
          return next;
        });
        toast.error(t('account.settings.notifications.spaces.saveFailed'));
      }
    },
    [api, applyOptimistic, toast, t],
  );

  const setNotificationDefaults = useCallback(
    async (defaults: SpaceNotificationDefaults) => {
      const prev = notificationDefaultsRef.current;
      const seq = defaultsRequestSeq.current + 1;
      defaultsRequestSeq.current = seq;
      setNotificationDefaultsState(defaults);
      try {
        const res = await api.identity.updateNotificationSettings({ spaces: defaults });
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'save failed');
        if (defaultsRequestSeq.current === seq) setNotificationDefaultsState(res.data.spaces);
        emitSpaceNotificationSettingsSaved(null);
      } catch {
        if (defaultsRequestSeq.current !== seq) return;
        setNotificationDefaultsState(prev);
        toast.error(t('account.settings.notifications.spaces.saveFailed'));
      }
    },
    [api, toast, t],
  );

  const setNotificationDisplay = useCallback(
    async (display: NotificationDisplaySettings) => {
      const prev = notificationDisplayRef.current;
      const seq = displayRequestSeq.current + 1;
      displayRequestSeq.current = seq;
      setNotificationDisplayState(display);
      try {
        const res = await api.identity.updateNotificationSettings({ display });
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'save failed');
        if (displayRequestSeq.current === seq) {
          setNotificationDisplayState(resolveNotificationDisplay(res.data.display));
        }
      } catch {
        if (displayRequestSeq.current !== seq) return;
        setNotificationDisplayState(prev);
        toast.error(t('account.settings.notifications.details.saveFailed'));
      }
    },
    [api, toast, t],
  );

  const getThemeOverrideCount = useCallback(async () => {
    const res = await api.spaces.getThemeOverrides();
    if (!res.success || !res.data) throw new Error(res.error?.message ?? 'count failed');
    return res.data.count;
  }, [api]);

  const resetThemeOverrides = useCallback(async () => {
    const res = await api.spaces.resetThemeOverrides();
    if (!res.success || !res.data) throw new Error(res.error?.message ?? 'reset failed');
    themeRequestSeq.current = {};
    setPreferences((prev) => {
      let changed = false;
      const next: PreferencesMap = {};
      for (const [spaceId, pref] of Object.entries(prev)) {
        if (pref.useSpaceTheme === undefined) {
          next[spaceId] = pref;
        } else {
          const { useSpaceTheme: _cleared, ...rest } = pref;
          next[spaceId] = rest;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    return res.data.count;
  }, [api]);

  const value = useMemo<SpacePreferencesContextValue>(
    () => ({
      preferences,
      loading,
      toggleFavorite,
      setUseSpaceTheme,
      getThemeOverrideCount,
      resetThemeOverrides,
      notificationDefaults,
      setNotificationDefaults,
      setSpaceNotifications,
      setActivityPreferences,
      notificationDisplay,
      setNotificationDisplay,
    }),
    [
      preferences,
      loading,
      toggleFavorite,
      setUseSpaceTheme,
      getThemeOverrideCount,
      resetThemeOverrides,
      notificationDefaults,
      setNotificationDefaults,
      setSpaceNotifications,
      setActivityPreferences,
      notificationDisplay,
      setNotificationDisplay,
    ],
  );

  return (
    <SpacePreferencesContext.Provider value={value}>{children}</SpacePreferencesContext.Provider>
  );
}

export function useSpacePreferences(): SpacePreferencesContextValue {
  const ctx = useContext(SpacePreferencesContext);
  if (!ctx) {
    throw new Error('useSpacePreferences must be used within a SpacePreferencesProvider');
  }
  return ctx;
}

/** Like {@link useSpacePreferences}, but null outside the provider (public shell). */
export function useOptionalSpacePreferences(): SpacePreferencesContextValue | null {
  return useContext(SpacePreferencesContext);
}
